import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, {SelectChangeEvent} from "@mui/material/Select";
import {Box, Button, Divider, useMediaQuery} from "@mui/material";
import {useTranslation} from "react-i18next";

import {
    resetCenter,
    resetYear,
    resetZoom,
    setSelectedCountry,
    setSelectedCountryBelongsToOtherRegion,
    setYear,
} from "../../../../features/map/mapSlice";
import {resetPins} from "../../../../features/map/pinsSlice";

import {AppDispatch, RootState} from "../../../../store/store";
import {availableYears, defaultYear, regionEnv} from "../../../../config";

import theme from "../../../../components/styles/theme/theme";

export const SelectButtons: React.FC = () => {
    const {t} = useTranslation();
    const isSmScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const dispatch = useDispatch<AppDispatch>();
    const {
        map: {selectedCountry, year, defaultCountry},
        countriesPerUser: {data: countriesData},
        statistics: {data: statisticsData},
    } = useSelector((state: RootState) => state);

    const hideMap = () => {
        const mapContainer = document.getElementById('map-container');
        if (mapContainer && mapContainer.style) {
            mapContainer.style.visibility = 'hidden';
            setTimeout(() => {
                mapContainer.style.visibility = 'unset';
            }, 400);
        }
    };

    const optionsData = [...countriesData];

    const [isYearSelectVisible, setIsYearSelectVisible] = useState(false);


    useEffect(() => {
        availableYears.length > 1
            ? setIsYearSelectVisible(true)
            : setIsYearSelectVisible(false);
    }, []);

    const handleCountryChange = (event: SelectChangeEvent) => {
        const {value} = event.target;
        const selectedCountry = countriesData.find(
            (country) => country.country_name.toUpperCase() === value.toUpperCase()
        );
        hideMap();
        dispatch(setSelectedCountry(selectedCountry));
        dispatch(setSelectedCountryBelongsToOtherRegion(undefined));
        dispatch(resetPins());
        dispatch(resetZoom());
        dispatch(resetCenter());
    };
    const handleYearChange = (event: SelectChangeEvent) => {
        const {value} = event.target;
        dispatch(setYear(value));
        dispatch(resetPins());
        dispatch(resetZoom());
        dispatch(resetCenter());
    };

    const handleReset = () => {
        dispatch(setSelectedCountry(defaultCountry));
        dispatch(setSelectedCountryBelongsToOtherRegion(undefined));
        dispatch(resetYear());
        dispatch(resetZoom());
        dispatch(resetCenter());
        dispatch(resetPins());
    };

    React.useEffect(() => {
        const countryExistInCurrentYear = !!(selectedCountry && statisticsData && statisticsData.find(
            (country) => {
                return (country.country_name || '').toUpperCase() === (selectedCountry.country_name || '').toUpperCase()
            }
        ));
        if (!countryExistInCurrentYear) {
            hideMap();
            dispatch(setSelectedCountry(defaultCountry));
            dispatch(setSelectedCountryBelongsToOtherRegion(undefined));
        }
    }, [defaultCountry, dispatch, selectedCountry, statisticsData, year]);

    const isDifferentCountry =
        selectedCountry &&
        selectedCountry?.country_name.toUpperCase() !== regionEnv;
    const isYearNotDefault = defaultYear !== year;

    const isResetVisible = isDifferentCountry || isYearNotDefault;

    return (
        <div className='hidden-print'>
            <Box
                gridColumn="span 12"
                display="flex"
                alignItems="center"
                justifyContent="end"
                flexDirection={isSmScreen ? "column" : "row"}
            >
                {isResetVisible && (
                    <>
                        <FormControl sx={{m: 1, p: 1}} size="small">
                            <Button
                                sx={{
                                    color: "custom.blue",
                                    width: "100px",
                                    lineHeight: "24px",
                                    textTransform: "none",
                                }}
                                onClick={handleReset}
                            >
                                {t("common.reset_view")}
                            </Button>
                        </FormControl>
                        <Divider orientation="vertical" flexItem/>
                    </>
                )}
                {isYearSelectVisible && (

                    <FormControl
                        sx={{
                            m: 1,
                            p: 1,
                            minWidth: "100px",
                            "& .MuiInputLabel-root": {
                                color: "primary.500",
                                top: "8px",
                                left: "8px",
                            },
                            "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                    borderColor: "primary.300",
                                },
                            },
                            ".MuiInputBase-input": {
                                color: "primary.500",
                            },
                        }}
                        size="small"
                    >
                        <InputLabel id="year-label"> {t("common.year_label")}</InputLabel>
                        <Select
                            labelId="year-label"
                            id="year-select"
                            value={year}
                            label="Year"
                            onChange={handleYearChange}
                            sx={{maxHeight: 300}}
                        >
                            {availableYears.map((year) => {
                                return (
                                    <MenuItem key={year} value={year}>
                                        {year}
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>

                )}
                {optionsData.length > 1 && (
                    <FormControl
                        sx={{
                            m: 1,
                            p: 1,
                            minWidth: 180,
                            "& .MuiInputLabel-root": {
                                color: "primary.500",
                                top: "8px",
                                left: "8px",
                            },
                            "& .MuiOutlinedInput-root": {
                                "& fieldset": {
                                    borderColor: "primary.300",
                                },
                            },
                            ".MuiInputBase-input": {
                                color: "primary.500",
                            },
                        }}
                        size="small"
                    >
                        <InputLabel id="country-label">
                            {" "}
                            {t("common.country_label")}
                        </InputLabel>
                        <Select
                            labelId="country-label"
                            id="country-select"
                            value={selectedCountry ? selectedCountry.country_name : ""}
                            label="Country"
                            onChange={handleCountryChange}
                            MenuProps={{PaperProps: {sx: {maxHeight: 300}}}}
                        >
                            {optionsData
                                .sort((a, b) => a.country_name.localeCompare(b.country_name))
                                .map((country) => (
                                    <MenuItem key={country.iso3} value={country.country_name}>
                                        {country.country_name}
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                )}
            </Box>
        </div>


    );
};
